/* ============================================================================
   Auth module — public "reset password" page.
   Rendered outside the authenticated shell (publicRoute) at /reset-password.
   Reads the one-time ?token= from the emailed link, collects a new password
   (+ confirmation), and posts to /api/auth/reset-password. On success it shows
   a confirmation with a link back to sign in.

   Client-side pre-checks (non-empty, new === confirm, min-length hint) catch
   the obvious cases; the server enforces the live (DB-driven) password policy
   and returns the authoritative error — including "invalid or expired reset
   link" when the token is bad.
   ============================================================================ */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiPost, isApiError } from '../../lib/api';

/** Soft client-side floor. The server enforces the real (DB-driven) policy. */
const MIN_LENGTH = 10;

type Status = 'idle' | 'busy' | 'error' | 'success';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const busy = status === 'busy';
  const done = status === 'success';

  async function submit() {
    setError(null);

    if (!token) {
      setStatus('error');
      setError('This reset link is missing its token. Please use the link from your email, or request a new one.');
      return;
    }
    if (!next || !confirm) {
      setStatus('error');
      setError('Enter and confirm your new password.');
      return;
    }
    if (next.length < MIN_LENGTH) {
      setStatus('error');
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setStatus('error');
      setError('New password and confirmation do not match.');
      return;
    }

    setStatus('busy');
    try {
      await apiPost('/api/auth/reset-password', { token, newPassword: next });
      setStatus('success');
    } catch (e) {
      setStatus('error');
      setError(isApiError(e) ? e.message : 'Could not reset your password.');
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--ground)',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
      }}
    >
      <div className="card card-pad-lg" style={{ width: '100%', maxWidth: 440 }}>
        {done ? (
          <div className="stack">
            <p className="eyebrow" style={{ marginTop: 0 }}>
              Password reset
            </p>
            <h2 style={{ marginTop: 0, marginBottom: 6 }}>You're all set</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Your password has been updated. Use it the next time you sign in.
            </p>
            <button className="btn btn-primary btn-block" onClick={() => navigate('/')}>
              Go to sign in
            </button>
          </div>
        ) : (
          <>
            <p className="eyebrow" style={{ marginTop: 0 }}>
              UVA Creative Requests
            </p>
            <h2 style={{ marginTop: 0, marginBottom: 6 }}>Choose a new password</h2>
            <p className="muted" style={{ marginTop: 0, marginBottom: 22 }}>
              Set a new password for your account below.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!busy) void submit();
              }}
              noValidate
            >
              {error && (
                <div className="error-text" role="alert" style={{ marginBottom: 14 }}>
                  {error}
                </div>
              )}

              <div className="field">
                <label htmlFor="rp-new">New password</label>
                <input
                  id="rp-new"
                  className="in"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  autoFocus
                />
                <span className="hint">Use at least {MIN_LENGTH} characters.</span>
              </div>

              <div className="field">
                <label htmlFor="rp-confirm">Confirm new password</label>
                <input
                  id="rp-confirm"
                  className="in"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={busy || !next || !confirm}
              >
                {busy ? 'Saving…' : 'Reset password'}
              </button>
            </form>

            <p className="meta" style={{ marginTop: 22, textAlign: 'center' }}>
              <button
                type="button"
                className="request-access-link"
                onClick={() => navigate('/')}
              >
                Back to sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
